import React from 'react';
import { Check, ChevronDown, Lock } from 'lucide-react';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import ConfirmDialog from '../ui/ConfirmDialog';
import { getStatusVariant } from './heatStatus';
import type { Heat } from '../../types/heats';
import { cn } from '../../lib/utils';

/**
 * Le statut d'une vague se règle SUR SA LIGNE, jamais dans son formulaire.
 *
 * Il vivait dans `HeatFormDialog` sous la forme d'un `Select` à SEPT valeurs,
 * `running` et `finished` compris — des états que le terrain écrit pendant la
 * course. Corriger une heure et publier la vague au planning public était donc
 * le même clic, et rien ne les distinguait.
 *
 * C'est le geste déjà retenu pour le PALIER d'un WOD, sorti de son formulaire
 * le 2026-09-14 pour la même raison : `PUT /wods` effaçait `mystery_at` /
 * `public_at` au passage, et corriger le nom d'un WOD public le faisait
 * retomber en brouillon pour tout le monde. Ce qui rend public se règle à un
 * endroit qui ne fait que ça.
 *
 * La pastille DIT déjà le statut sur chaque ligne du planning ; elle devient
 * l'endroit où on le change.
 */

/** Ce que l'organisateur décide : la planification. Miroir de `STATUTS_CIBLES`. */
export const STATUTS_CHOISISSABLES = ['scheduled', 'ready', 'cancelled'] as const;

/**
 * Ce que le TERRAIN écrit. Miroir EXACT de `core/heat_status.py::
 * STATUTS_VERROUILLES` — si les deux listes divergent, l'écran proposera un
 * changement que le serveur refusera, ou taira une action qu'il accepterait.
 */
const VERROUILLES = new Set(['preparing', 'countdown', 'running', 'finished', 'completed']);

export const estStatutVerrouille = (status?: string): boolean =>
  VERROUILLES.has((status || '').toLowerCase());

/**
 * (2026-09-27) Une fois l'ÉVÉNEMENT passé, le verrou ne protège plus rien :
 * il empêche seulement de refermer une vague restée « en cours » parce qu'un
 * athlète n'a jamais eu de temps d'arrivée. Le menu propose alors aussi
 * « Terminé », l'état vrai d'une vague courue. Miroir de
 * `core/heat_status.py::statuts_cibles_pour`.
 */
export const estEvenementTermine = (dateEnd?: string | null): boolean => {
  if (!dateEnd) return false;
  const fin = new Date(dateEnd.substring(0, 10) + 'T23:59:59');
  return !Number.isNaN(fin.getTime()) && fin.getTime() < Date.now();
};

interface Props {
  heat: Heat;
  /** Libellé traduit d'un statut. */
  statusLabel: (s: string) => string;
  /** Absent ⇒ pastille en lecture seule (l'appelant n'autorise pas le changement). */
  onChangeStatus?: (heat: Heat, status: string) => void;
  /** Événement passé : le verrou tombe et « Terminé » devient une cible. */
  eventOver?: boolean;
  t: (key: string, options?: Record<string, unknown>) => string;
}

const HeatStatusChip: React.FC<Props> = ({ heat, statusLabel, onChangeStatus, eventOver = false, t }) => {
  const [confirmCancel, setConfirmCancel] = React.useState(false);
  const statut = (heat.status || '').toLowerCase();
  const verrouille = !eventOver && estStatutVerrouille(statut);
  const cibles: readonly string[] = eventOver ? [...STATUTS_CHOISISSABLES, 'finished'] : STATUTS_CHOISISSABLES;

  // Une vague en cours ou terminée porte des résultats : son statut vient du
  // terrain, on ne le repose pas depuis un écran de planification. Elle reste
  // LISIBLE, simplement pas cliquable — la masquer perdrait l'information.
  if (!onChangeStatus || verrouille) {
    return (
      <Badge
        variant={getStatusVariant(heat.status)}
        className="gap-1 py-0 text-[10px]"
        title={verrouille ? t('heats.statusChip.locked') : undefined}
      >
        {verrouille && <Lock size={9} />}
        {statusLabel(heat.status)}
      </Badge>
    );
  }

  const choisir = (cible: string) => {
    if (cible === statut) return;
    // Annuler une vague n'est pas du même ordre que la publier : elle sort du
    // planning public et ses inscrits n'ont plus d'horaire. On le demande.
    if (cible === 'cancelled') {
      setConfirmCancel(true);
      return;
    }
    onChangeStatus(heat, cible);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            data-tour="heats-status"
            onClick={e => e.stopPropagation()}
            aria-label={t('heats.statusChip.trigger', { status: statusLabel(heat.status) })}
            className="rounded-[2px] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/55"
            /* ring BLANC et non `ring-ring` : ce token vaut l'or de la
               marque, que la charte réserve aux CTA et au rang 1. Un
               cerclage de focus est un état d'écran, donc blanc. */
          >
            <Badge
              variant={getStatusVariant(heat.status)}
              className={cn(
                'gap-1 py-0 text-[10px] transition-colors',
                'hover:border-foreground/55 hover:bg-foreground/[0.06]',
              )}
            >
              {statusLabel(heat.status)}
              <ChevronDown size={9} />
            </Badge>
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="start" onClick={e => e.stopPropagation()} className="w-64">
          <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-muted-foreground">
            {t('heats.statusChip.title')}
          </DropdownMenuLabel>

          {cibles.map(cible => (
            <DropdownMenuItem
              key={cible}
              onSelect={() => choisir(cible)}
              /* Sélection = voile blanc. Le survol vient du composant
                 partagé, corrigé à la racine le 2026-09-22 : `bg-accent` vaut
                 l'or de la marque, que la charte réserve aux CTA et au rang 1. */
              className={cn('gap-2', cible === statut && 'bg-white/[0.07]')}
            >
              <span className="w-3.5 shrink-0">
                {cible === statut && <Check size={14} />}
              </span>
              <span className={cn('text-sm', cible === statut && 'font-semibold')}>
                {statusLabel(cible)}
              </span>
              <span className="ml-auto text-[11px] text-muted-foreground">
                {t(`heats.statusChip.hint.${cible}`)}
              </span>
            </DropdownMenuItem>
          ))}

          <DropdownMenuSeparator />
          {eventOver && (
            <p className="px-2 py-1.5 text-[11px] leading-relaxed text-muted-foreground">
              {t('heats.statusChip.eventOver')}
            </p>
          )}
          {/* Ce que ce menu ne fait PAS : prévenir les athlètes. Publier ici
              corrige un statut ; l'email reste le travail de « Publier », qui
              montre ses destinataires avant d'écrire à qui que ce soit. */}
          <p className="px-2 py-1.5 text-[11px] leading-relaxed text-muted-foreground">
            {t('heats.statusChip.noEmail')}
          </p>
        </DropdownMenuContent>
      </DropdownMenu>

      <ConfirmDialog
        open={confirmCancel}
        onOpenChange={setConfirmCancel}
        destructive
        title={t('heats.statusChip.cancelTitle')}
        message={t('heats.statusChip.cancelMessage', { number: heat.heat_number })}
        description={t('heats.statusChip.cancelDescription')}
        confirmLabel={t('heats.statusChip.cancelConfirm')}
        onConfirm={() => {
          setConfirmCancel(false);
          onChangeStatus(heat, 'cancelled');
        }}
      />
    </>
  );
};

export default HeatStatusChip;
